// @ts-nocheck
import { Button } from "@/components/ui/button";
import { generateReportPdf, downloadPdf } from '@/utils/reportPdf';
import { isClientCourtCost } from '@/utils/courtCosts';
import { buildOfficeReport, formatHours, parseLocalDate, reportTitle } from '@/utils/officeReport';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useMemo } from "react";
import { BarChart, PieChart, Loader2, ArrowUpDown, Download } from "lucide-react";
import { Bar, BarChart as RechartsBarChart, Pie, PieChart as RechartsPieChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { startOfMonth, endOfMonth, subMonths, startOfYear } from "date-fns";
import Papa from 'papaparse';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Heart, Users, Calendar, TrendingUp, Scale, UserPlus, Clock, CheckCircle2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 10;

// Compara nomes ignorando espaços, acentos e maiúsculas
// Mesma pessoa = mesmo primeiro nome + mesma inicial do sobrenome
// (ex.: "Allana S.", "Allana Sth\u00e9fany" e "Allana Sthefany Pereira Viana")
const personKey = (name?: string | null) => {
  const tokens = String(name || '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().split(/\s+/).filter(Boolean);
  return tokens.length ? `${tokens[0]} ${tokens[1]?.[0] || ''}`.trim() : '';
};
// Entre as formas do mesmo nome, exibe a mais curta
const shorterName = (current: string | undefined, candidate: string) =>
  !current || candidate.length < current.length ? candidate : current;

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [financialSummary, setFinancialSummary] = useState({ revenue: 0, expenses: 0, profit: 0 });
  const [casesByStatus, setCasesByStatus] = useState<any[]>([]);
  const [detailedCases, setDetailedCases] = useState<any[]>([]);
  const [lawyerPerformance, setLawyerPerformance] = useState<any[]>([]);
  const navigate = useNavigate();
  const [officeReport, setOfficeReport] = useState<any>(null);
  // Por padrão, o mês atual inteiro
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: SortDirection }>({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [caseTypeFilter, setCaseTypeFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [lawyerFilter, setLawyerFilter] = useState<string | undefined>(undefined);
  const [clientFilter, setClientFilter] = useState<string | undefined>(undefined);
  const [keywordFilter, setKeywordFilter] = useState<string>("");

  const [realLawyers, setRealLawyers] = useState<string[]>([]);
  const [realClients, setRealClients] = useState<{ id: string; name: string }[]>([]);
  const [proBonoData, setProBonoData] = useState<any[]>([]);
  const [proBonoSummary, setProBonoSummary] = useState({ totalCases: 0, totalValue: 0, monthlyEvolution: [] });

  // Tipos reais de casos do sistema
  const caseTypes = [
    "Processo Administrativo", 
    "Ação Civil", 
    "Procedimentos da Fazenda", 
    "Trabalhista", 
    "Criminal",
    "Família",
    "Sucessões",
    "Trabalhista",
    "Tributário",
    "Previdenciário"
  ];
  
  // Status reais dos casos do sistema
  const statuses = [
    "Em andamento", 
    "Distribuído", 
    "Aguardando documentos", 
    "Aguardando sentença", 
    "Transitado em julgado", 
    "Concluído", 
    "Suspenso",
    "Arquivado"
  ];

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);

      // Buscar todos os dados e filtrar no frontend
      const { data: rawFinancialData } = await supabase.from('financial_entries').select('entry_type, amount, payment_date, category, description, case_id, administrative_process_id, status');
      // Custas pagas pelo cliente não entram nos números do escritório
      const allFinancialData = (rawFinancialData || []).filter(entry => !isClientCourtCost(entry));
      const { data: allCasesData, error: allCasesError } = await supabase.from('cases').select('*, clients:client_id(name), lawyer_responsible');
      
      // Buscar processos administrativos
      const { data: allAdminProcessesData, error: adminProcessesError } = await supabase
        .from('administrative_processes')
        .select('*, clients:client_id(name), lawyer_responsible');
      
      // Buscar tarefas para relatório de produtividade
      const { data: allTasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('*, cases:case_id(title), clients:client_id(name)');
      
      // Buscar compromissos para relatório de agenda
      const { data: allAppointmentsData, error: appointmentsError } = await supabase
        .from('appointments')
        .select('*, clients:client_id(name), cases:case_id(title)');

      // Buscar dados Pro Bono
      const { data: proBonoFinancialData, error: proBonoError } = await supabase
        .from('financial_entries')
        .select('*, cases:case_id(title, client_id, created_at), clients:client_id(name)')
        .eq('entry_type', 'probono');

      // Fetch real lawyers
      const { data: lawyersData, error: lawyersError } = await supabase
        .from('cases')
        .select('lawyer_responsible', { distinct: true })
        .not('lawyer_responsible', 'is', null);
      if (lawyersData) {
        // Um nome por responsável (junta as variações do mesmo nome)
        const byKey = new Map<string, string>();
        lawyersData.forEach(item => {
          const name = String(item.lawyer_responsible || '').trim();
          if (!name) return;
          const key = personKey(name);
          byKey.set(key, shorterName(byKey.get(key), name));
        });
        setRealLawyers([...byKey.values()].sort((a, b) => a.localeCompare(b, 'pt-BR')));
      }

      // Fetch real clients
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('id, name, created_at');
      if (clientsData) {
        setRealClients(clientsData);
      }

      // Filtrar dados financeiros por data
      let filteredFinancialData = allFinancialData || [];
      if (dateRange?.from || dateRange?.to) {
        filteredFinancialData = allFinancialData?.filter(entry => {
          const entryDateLocal = parseLocalDate(entry.payment_date);
          if (!entryDateLocal) return false;
          
          if (dateRange?.from) {
            const fromDateLocal = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
            if (entryDateLocal.getTime() < fromDateLocal.getTime()) return false;
          }
          
          if (dateRange?.to) {
            const toDateLocal = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
            if (entryDateLocal.getTime() > toDateLocal.getTime()) return false;
          }
          
          return true;
        }) || [];
      }

      // Filtrar casos por data
      let filteredCasesData = allCasesData || [];
      if (dateRange?.from || dateRange?.to) {
        filteredCasesData = allCasesData?.filter(case_ => {
          const caseDate = new Date(case_.created_at);
          const caseDateLocal = new Date(caseDate.getFullYear(), caseDate.getMonth(), caseDate.getDate());
          
          if (dateRange?.from) {
            const fromDateLocal = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
            if (caseDateLocal.getTime() < fromDateLocal.getTime()) return false;
          }
          
          if (dateRange?.to) {
            const toDateLocal = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
            if (caseDateLocal.getTime() > toDateLocal.getTime()) return false;
          }
          
          return true;
        }) || [];
      }
      
      // Filtrar processos administrativos por data
      let filteredAdminProcessesData = allAdminProcessesData || [];
      if (dateRange?.from || dateRange?.to) {
        filteredAdminProcessesData = allAdminProcessesData?.filter(process => {
          const processDate = new Date(process.created_at);
          const processDateLocal = new Date(processDate.getFullYear(), processDate.getMonth(), processDate.getDate());
          
          if (dateRange?.from) {
            const fromDateLocal = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
            if (processDateLocal.getTime() < fromDateLocal.getTime()) return false;
          }
          
          if (dateRange?.to) {
            const toDateLocal = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
            if (processDateLocal.getTime() > toDateLocal.getTime()) return false;
          }
          
          return true;
        }) || [];
      }
      
      // Combinar casos regulares com processos administrativos
      const allProcessesData = [
        ...(filteredCasesData || []).map(case_ => ({
          ...case_,
          process_type: 'case',
          display_title: case_.title,
          display_type: case_.case_type
        })),
        ...(filteredAdminProcessesData || []).map(process => ({
          ...process,
          process_type: 'administrative',
          display_title: process.title || process.description,
          display_type: 'Processo Administrativo',
          case_type: 'Processo Administrativo'
        }))
      ];

      // Aplicar filtros adicionais aos processos combinados
      let finalProcessesData = allProcessesData;
      
      if (caseTypeFilter) {
        finalProcessesData = finalProcessesData.filter(process => process.display_type === caseTypeFilter);
      }
      if (statusFilter) {
        finalProcessesData = finalProcessesData.filter(process => process.status === statusFilter);
      }
      if (lawyerFilter) {
        finalProcessesData = finalProcessesData.filter(process => personKey(process.lawyer_responsible) === personKey(lawyerFilter));
      }
      if (clientFilter) {
        finalProcessesData = finalProcessesData.filter(process => process.client_id === clientFilter);
      }
      if (keywordFilter) {
        finalProcessesData = finalProcessesData.filter(process => 
          process.display_title.toLowerCase().includes(keywordFilter.toLowerCase())
        );
      }
      
      // Manter compatibilidade com código existente
      let finalCasesData = finalProcessesData.filter(process => process.process_type === 'case');

      // Processar dados financeiros
      if (filteredFinancialData) {
        const revenue = filteredFinancialData.filter(e => e.entry_type === 'receita').reduce((acc, e) => acc + e.amount, 0);
        const expenses = filteredFinancialData.filter(e => e.entry_type === 'despesa').reduce((acc, e) => acc + e.amount, 0);
        setFinancialSummary({ revenue, expenses, profit: revenue - expenses });
      }

      // Processar dados dos processos (casos + administrativos)
      if (finalProcessesData) {
        setDetailedCases(finalProcessesData);
        const statusCounts = finalProcessesData.reduce((acc, process) => {
          const status = process.status || 'Não definido';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const chartData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
        setCasesByStatus(chartData);
      }

      // Processar performance dos advogados
      if (finalProcessesData) {
        const lawyerStats: { [key: string]: { casesCount: number; totalResolutionTime: number; completedCases: number } } = {};

        const lawyerDisplayName: Record<string, string> = {};
        finalProcessesData.forEach(process => {
          const rawName = String(process.lawyer_responsible || '').trim() || 'Não atribuído';
          lawyerDisplayName[personKey(rawName)] = shorterName(lawyerDisplayName[personKey(rawName)], rawName);
        });
        finalProcessesData.forEach(process => {
          const rawName = String(process.lawyer_responsible || '').trim() || 'Não atribuído';
          const lawyer = lawyerDisplayName[personKey(rawName)];
          if (!lawyerStats[lawyer]) {
            lawyerStats[lawyer] = { casesCount: 0, totalResolutionTime: 0, completedCases: 0 };
          }
          lawyerStats[lawyer].casesCount++;

          if (process.status === 'Concluído' && process.created_at && process.updated_at) {
            const createdAt = new Date(process.created_at).getTime();
            const updatedAt = new Date(process.updated_at).getTime();
            const resolutionTime = updatedAt - createdAt; // Time in milliseconds
            lawyerStats[lawyer].totalResolutionTime += resolutionTime;
            lawyerStats[lawyer].completedCases++;
          }
        });

        const performanceData = Object.entries(lawyerStats).map(([lawyer, stats]) => ({
          lawyer,
          casesCount: stats.casesCount,
          averageResolutionTime: stats.completedCases > 0
            ? (stats.totalResolutionTime / stats.completedCases / (1000 * 60 * 60 * 24)).toFixed(2) // Convert ms to days
            : '—',
        }));
        setLawyerPerformance(performanceData);
      }

      // Processar dados Pro Bono
      if (proBonoFinancialData) {
        setProBonoData(proBonoFinancialData);
        
        // Calcular resumo Pro Bono
        const totalCases = proBonoFinancialData.length;
        const totalValue = proBonoFinancialData.reduce((acc, entry) => acc + (entry.amount || 0), 0);
        
        // Evolução mensal Pro Bono
        const monthlyData = proBonoFinancialData.reduce((acc, entry) => {
          const month = new Date(entry.created_at).toISOString().substring(0, 7);
          if (!acc[month]) {
            acc[month] = { month, cases: 0, value: 0 };
          }
          acc[month].cases += 1;
          acc[month].value += entry.amount || 0;
          return acc;
        }, {} as Record<string, { month: string; cases: number; value: number }>);
        
        const monthlyEvolution = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
        
        setProBonoSummary({ totalCases, totalValue, monthlyEvolution });
      }

      // Visão geral do escritório (processos, clientes, atendimentos, horas e financeiro mês a mês)
      setOfficeReport(buildOfficeReport({
        from: dateRange?.from,
        to: dateRange?.to || dateRange?.from,
        cases: allCasesData || [],
        adminProcesses: allAdminProcessesData || [],
        clients: clientsData || [],
        tasks: allTasksData || [],
        appointments: allAppointmentsData || [],
        financial: allFinancialData,
      }));

      if (lawyersError || clientsError || allCasesError || adminProcessesError || tasksError || appointmentsError || proBonoError) {
        console.error("Error fetching report data:", { 
          lawyersError, 
          clientsError, 
          allCasesError, 
          adminProcessesError, 
          tasksError, 
          appointmentsError 
        });
      }

      setLoading(false);
    };

    fetchReportData();
  }, [dateRange, caseTypeFilter, statusFilter, lawyerFilter, clientFilter, keywordFilter]);

  const sortedCases = useMemo(() => {
    let sortableItems = [...detailedCases];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [detailedCases, sortConfig]);

  const paginatedCases = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedCases.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedCases, currentPage]);

  const totalPages = Math.ceil(sortedCases.length / ITEMS_PER_PAGE);

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const [generatingPdf, setGeneratingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    try {
      const filters = [
        caseTypeFilter && `Tipo: ${caseTypeFilter}`,
        statusFilter && `Status: ${statusFilter}`,
        lawyerFilter && `Responsável: ${lawyerFilter}`,
        clientFilter && `Cliente: ${realClients.find(c => c.id === clientFilter)?.name || 'selecionado'}`,
        keywordFilter && `Busca: ${keywordFilter}`,
      ].filter(Boolean) as string[];
      const bytes = await generateReportPdf({
        periodFrom: dateRange?.from,
        periodTo: dateRange?.to,
        filters,
        financial: financialSummary,
        casesByStatus,
        proBono: {
          totalCases: proBonoSummary.totalCases,
          totalValue: proBonoSummary.totalValue,
          activeMonths: proBonoSummary.monthlyEvolution.length,
        },
        cases: sortedCases.map(c => ({
          title: c.display_title || c.title || 'Sem título',
          client: c.clients?.name || '-',
          type: c.display_type || c.case_type || '-',
          status: c.status || '-',
          createdAt: new Date(c.created_at).toLocaleDateString('pt-BR'),
        })),
        lawyers: lawyerPerformance,
        office: officeReport,
      });
      const stamp = new Date().toISOString().slice(0, 10);
      downloadPdf(bytes, `relatorio-allana-sthefany-${stamp}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleExport = () => {
    const financialCsv = Papa.unparse([
      {
        "Tipo": "Receita",
        "Valor": financialSummary.revenue
      },
      {
        "Tipo": "Despesa",
        "Valor": financialSummary.expenses
      },
      {
        "Tipo": "Lucro",
        "Valor": financialSummary.profit
      }
    ]);

    const casesCsv = Papa.unparse(sortedCases.map(c => ({
      "Título": c.display_title || c.title || 'N/A',
      "Tipo": c.display_type || c.case_type || 'N/A',
      "Cliente": c.clients?.name || 'N/A',
      "Status": c.status || 'N/A',
      "Data de Criação": new Date(c.created_at).toLocaleDateString('pt-BR')
    })));

    const combinedCsv = `Relatório Financeiro\n${financialCsv}\n\nRelatório de Processos\n${casesCsv}`;

    const blob = new Blob([combinedCsv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "relatorio.csv");
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF'];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Concluído": return "bg-tertiary text-tertiary-foreground";
      case "Em andamento": return "bg-primary text-primary-foreground";
      case "Aguardando documentos": return "bg-warning text-warning-foreground";
      case "Suspenso": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="w-16 h-16 animate-spin" />
      </div>
    );
  }

  const brl = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  const ALL = '__all';
  const filterTrigger = 'h-10 rounded-xl border-slate-200 bg-white text-sm text-slate-700 shadow-none';
  const processLink = (case_: any) => case_.process_type === 'administrative' ? `/administrative-process/${case_.id}` : `/processo/${case_.id}`;
  const statusTone = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('andamento')) return 'bg-emerald-50 text-emerald-700';
    if (s.includes('aguardando')) return 'bg-amber-50 text-amber-700';
    if (s.includes('conclu')) return 'bg-slate-100 text-slate-600';
    if (s.includes('suspen')) return 'bg-orange-50 text-orange-700';
    return 'bg-blue-50 text-blue-700';
  };
  const PIE_COLORS = ['#c4ab83', '#0f172a', '#10b981', '#f59e0b', '#94a3b8', '#ef4444'];
  const flowTotal = financialSummary.revenue + financialSummary.expenses;
  const revenueShare = flowTotal > 0 ? (financialSummary.revenue / flowTotal) * 100 : 50;
  const SortHead = ({ field, label }: { field: string; label: string }) => (
    <TableHead onClick={() => requestSort(field)} className="cursor-pointer select-none whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 hover:text-slate-700">
      {label} <ArrowUpDown className="ml-1 inline-block h-3 w-3" />
    </TableHead>
  );

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 p-4 md:p-6">
      {/* Cabeçalho */}
      <header className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Gestão</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Relatórios</h1>
          <p className="mt-1 text-sm text-slate-500">Processos, clientes, atendimentos, horas e financeiro do escritório.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <div className="flex rounded-xl bg-slate-100 p-1">
            {[
              { label: 'Este mês', from: startOfMonth(new Date()), to: new Date() },
              { label: 'Mês passado', from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) },
              { label: 'Ano', from: startOfYear(new Date()), to: new Date() },
            ].map(preset => {
              const active = dateRange?.from?.toDateString() === preset.from.toDateString() && dateRange?.to?.toDateString() === preset.to.toDateString();
              return (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => setDateRange({ from: preset.from, to: preset.to })}
                  className={`rounded-lg px-3 py-1.5 text-[13px] transition-colors ${active ? 'bg-white font-semibold text-[#7a6440] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          <DateRangePicker date={dateRange} onDateChange={setDateRange} />
          <Button variant="outline" onClick={() => window.print()} className="h-10 rounded-xl border-slate-200 text-[13px] text-slate-700 shadow-none">
            Imprimir
          </Button>
          <Button onClick={handleDownloadPdf} disabled={generatingPdf} className="h-10 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800">
            {generatingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4 text-[#e8d5a8]" />}
            Baixar PDF
          </Button>
        </div>
      </header>

      {/* Filtros */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-2 print:hidden">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <Select onValueChange={(v) => setCaseTypeFilter(v === ALL ? undefined : v)} value={caseTypeFilter ?? ALL}>
            <SelectTrigger className={filterTrigger}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os tipos</SelectItem>
              {caseTypes.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => setStatusFilter(v === ALL ? undefined : v)} value={statusFilter ?? ALL}>
            <SelectTrigger className={filterTrigger}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os status</SelectItem>
              {statuses.map(status => <SelectItem key={status} value={status}>{status}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => setLawyerFilter(v === ALL ? undefined : v)} value={lawyerFilter ?? ALL}>
            <SelectTrigger className={filterTrigger}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os advogados</SelectItem>
              {realLawyers.map(lawyer => <SelectItem key={lawyer} value={lawyer}>{lawyer}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => setClientFilter(v === ALL ? undefined : v)} value={clientFilter ?? ALL}>
            <SelectTrigger className={filterTrigger}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os clientes</SelectItem>
              {realClients.filter(client => client.id !== '').map(client => (
                <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Buscar por palavra-chave..."
            value={keywordFilter}
            onChange={(e) => setKeywordFilter(e.target.value)}
            className="h-10 rounded-xl border-slate-200 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
          />
        </div>
      </div>

      {/* Visão geral do escritório */}
      {officeReport && (
        <>
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
            <div className="border-b border-slate-100 px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Visão geral</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">
                {reportTitle(dateRange?.from, dateRange?.to).subtitle
                  ? <>O escritório em <span className="capitalize">{reportTitle(dateRange?.from, dateRange?.to).subtitle}</span></>
                  : 'O escritório no período'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-px bg-slate-100 md:grid-cols-3 xl:grid-cols-6">
              {[
                { label: 'Processos novos', value: officeReport.totals.newProcesses, hint: `${officeReport.totals.newJudicial} judiciais · ${officeReport.totals.newAdministrative} administrativos`, icon: Scale },
                { label: 'Clientes novos', value: officeReport.totals.newClients, hint: 'cadastrados no período', icon: UserPlus },
                { label: 'Atendimentos', value: officeReport.totals.appointments, hint: `${officeReport.totals.appointmentsDone} concluído${officeReport.totals.appointmentsDone === 1 ? '' : 's'}${officeReport.totals.appointmentsCanceled ? ` · ${officeReport.totals.appointmentsCanceled} cancelado${officeReport.totals.appointmentsCanceled === 1 ? '' : 's'}` : ''}`, icon: Calendar },
                { label: 'Tempo trabalhado', value: formatHours(officeReport.totals.workedSeconds), hint: 'registrado nas tarefas', icon: Clock },
                { label: 'Tarefas concluídas', value: officeReport.totals.tasksCompleted, hint: `${officeReport.totals.tasksCreated} criadas no período`, icon: CheckCircle2 },
                { label: 'Resultado', value: brl(financialSummary.profit), hint: 'receitas menos despesas', icon: TrendingUp },
              ].map(tile => {
                const empty = tile.value === 0 || tile.value === '0h';
                return (
                  <div key={tile.label} className="bg-white px-5 py-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{tile.label}</p>
                      <tile.icon className="h-3.5 w-3.5 text-[#c4ab83]" />
                    </div>
                    <p className={`mt-1 truncate text-2xl font-semibold tabular-nums tracking-tight ${empty ? 'text-slate-300' : 'text-slate-900'}`}>{tile.value}</p>
                    <p className="mt-0.5 truncate text-[11px] text-slate-400">{tile.hint}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mês a mês */}
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
            <div className="border-b border-slate-100 px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Evolução</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Mês a mês</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70">
                  <tr className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    <th className="px-5 py-2.5 text-left">Mês</th>
                    <th className="px-3 py-2.5 text-right">Processos</th>
                    <th className="px-3 py-2.5 text-right">Clientes</th>
                    <th className="px-3 py-2.5 text-right">Atendimentos</th>
                    <th className="px-3 py-2.5 text-right">Tempo</th>
                    <th className="px-3 py-2.5 text-right">Tarefas</th>
                    <th className="px-3 py-2.5 text-right">Receitas</th>
                    <th className="px-3 py-2.5 text-right">Despesas</th>
                    <th className="px-5 py-2.5 text-right">Resultado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {officeReport.months.map((m: any) => {
                    const result = m.revenue - m.expenses;
                    const n = (v: number) => <span className={v ? 'text-slate-900' : 'text-slate-300'}>{v}</span>;
                    return (
                      <tr key={m.key} className="tabular-nums hover:bg-[#faf7f2]/60">
                        <td className="px-5 py-2.5 font-medium capitalize text-slate-800">{m.label}</td>
                        <td className="px-3 py-2.5 text-right">{n(m.processes)}</td>
                        <td className="px-3 py-2.5 text-right">{n(m.clients)}</td>
                        <td className="px-3 py-2.5 text-right">{n(m.appointments)}</td>
                        <td className={`px-3 py-2.5 text-right ${m.workedSeconds ? 'text-slate-900' : 'text-slate-300'}`}>{formatHours(m.workedSeconds)}</td>
                        <td className="px-3 py-2.5 text-right">{n(m.tasksCompleted)}</td>
                        <td className={`px-3 py-2.5 text-right ${m.revenue ? 'text-emerald-600' : 'text-slate-300'}`}>{brl(m.revenue)}</td>
                        <td className={`px-3 py-2.5 text-right ${m.expenses ? 'text-red-600' : 'text-slate-300'}`}>{brl(m.expenses)}</td>
                        <td className={`px-5 py-2.5 text-right font-semibold ${result > 0 ? 'text-emerald-600' : result < 0 ? 'text-red-600' : 'text-slate-300'}`}>{brl(result)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Atendimentos, tempo e clientes novos */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Agenda</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Atendimentos por tipo</p>
              {officeReport.appointmentsByType.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">Nenhum atendimento no período.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {officeReport.appointmentsByType.map((t: any) => (
                    <li key={t.name}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{t.name}</span>
                        <span className="font-semibold tabular-nums text-slate-900">{t.value}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-[#c4ab83]" style={{ width: `${(t.value / officeReport.appointmentsByType[0].value) * 100}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Produtividade</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Onde o tempo foi</p>
              {officeReport.topTasks.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">Nenhum tempo registrado no período.</p>
              ) : (
                <div className="mt-3 divide-y divide-slate-100">
                  {officeReport.topTasks.map((t: any, i: number) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <span className="truncate text-slate-700">{t.title}</span>
                      <span className="shrink-0 font-semibold tabular-nums text-slate-900">{formatHours(t.seconds)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Carteira</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Clientes novos</p>
              {officeReport.newClientsList.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">Nenhum cliente novo no período.</p>
              ) : (
                <div className="mt-3 max-h-72 divide-y divide-slate-100 overflow-y-auto">
                  {officeReport.newClientsList.map((c: any, i: number) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <span className="truncate text-slate-700">{c.name}</span>
                      <span className="shrink-0 tabular-nums text-xs text-slate-400">{c.date}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Resumo financeiro */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-3">
          {[
            { label: 'Receitas', value: financialSummary.revenue, className: financialSummary.revenue ? 'text-emerald-600' : 'text-slate-300' },
            { label: 'Despesas', value: financialSummary.expenses, className: financialSummary.expenses ? 'text-red-600' : 'text-slate-300' },
            { label: 'Resultado', value: financialSummary.profit, className: financialSummary.profit > 0 ? 'text-emerald-600' : financialSummary.profit < 0 ? 'text-red-600' : 'text-slate-300' },
          ].map(tile => (
            <div key={tile.label} className="bg-white px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{tile.label}</p>
              <p className={`mt-1 text-3xl font-semibold tabular-nums tracking-tight ${tile.className}`}>{brl(tile.value)}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 px-5 py-4">
          <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
            <div className="bg-emerald-500 transition-all duration-700" style={{ width: `${revenueShare}%` }} />
            <div className="bg-red-400 transition-all duration-700" style={{ width: `${100 - revenueShare}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Receitas x despesas no período (custas pagas pelo cliente não entram)</p>
        </div>
      </div>

      {/* Processos por status + Pro bono */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Processos</p>
          <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Por status</p>
          {casesByStatus.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">Nenhum processo no período.</p>
          ) : (
            <div className="mt-2 flex flex-col items-center gap-4 sm:flex-row">
              <div className="h-52 w-full sm:w-1/2">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie data={casesByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2} stroke="none">
                      {casesByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <ul className="w-full space-y-1.5 sm:w-1/2">
                {casesByStatus.map((entry, index) => (
                  <li key={entry.name} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-2 text-slate-600">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
                      {entry.name}
                    </span>
                    <span className="font-semibold tabular-nums text-slate-900">{entry.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Impacto social</p>
              <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Pro bono</p>
            </div>
            <Heart className="h-5 w-5 text-pink-400" />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200">
            {[
              { label: 'Casos', value: String(proBonoSummary.totalCases) },
              { label: 'Valor social', value: brl(proBonoSummary.totalValue) },
              { label: 'Meses ativos', value: String(proBonoSummary.monthlyEvolution.length) },
            ].map(tile => (
              <div key={tile.label} className="bg-white px-3 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{tile.label}</p>
                <p className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">{tile.value}</p>
              </div>
            ))}
          </div>
          {proBonoData.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Nenhum caso pro bono no período.</p>
          ) : (
            <div className="mt-3 divide-y divide-slate-100">
              {proBonoData.slice(0, 5).map((entry, index) => (
                <div key={index} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{entry.clients?.name || 'Cliente não informado'}</p>
                    <p className="truncate text-xs text-slate-500">{entry.cases?.title || 'Sem processo'}</p>
                  </div>
                  <span className="shrink-0 tabular-nums text-slate-700">{brl(entry.amount || 0)}</span>
                </div>
              ))}
              {proBonoData.length > 5 && (
                <button type="button" onClick={() => navigate('/probono-report')} className="pt-3 text-xs font-semibold text-[#9a7f52] hover:underline">
                  Ver todos os casos pro bono →
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Processos */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Detalhamento</p>
          <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Processos do período</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50/70">
              <TableRow className="border-slate-100">
                <SortHead field="display_title" label="Processo" />
                <SortHead field="clients" label="Cliente" />
                <SortHead field="display_type" label="Tipo" />
                <SortHead field="status" label="Status" />
                <SortHead field="lawyer_responsible" label="Responsável" />
                <SortHead field="created_at" label="Cadastro" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedCases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-slate-400">Nenhum processo encontrado com esses filtros.</TableCell>
                </TableRow>
              )}
              {paginatedCases.map((case_) => (
                <TableRow key={`${case_.process_type}-${case_.id}`} className="border-slate-100 hover:bg-[#faf7f2]/60">
                  <TableCell className="max-w-xs">
                    <Link to={processLink(case_)} className="block truncate font-medium text-slate-900 hover:text-[#9a7f52]">
                      {case_.display_title || case_.title || 'Sem título'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{case_.clients?.name || '—'}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${case_.process_type === 'administrative' ? 'bg-blue-50 text-blue-700' : 'bg-[#c4ab83]/15 text-[#7a6440]'}`}>
                      {case_.display_type || case_.case_type || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${statusTone(case_.status)}`}>{case_.status || '—'}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{case_.lawyer_responsible || '—'}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm tabular-nums text-slate-500">{new Date(case_.created_at).toLocaleDateString('pt-BR')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-xs text-slate-500 print:hidden">
          <span>Página {currentPage} de {Math.max(1, totalPages)}</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-8 rounded-lg" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
              ← Anterior
            </Button>
            <Button variant="ghost" size="sm" className="h-8 rounded-lg" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage >= totalPages}>
              Próxima →
            </Button>
          </div>
        </div>
      </div>

      {/* Advogados */}
      {lawyerPerformance.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
          <div className="border-b border-slate-100 px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Equipe</p>
            <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Desempenho por responsável</p>
          </div>
          <div className="divide-y divide-slate-100">
            {lawyerPerformance.map((data, index) => (
              <div key={index} className="flex items-center justify-between gap-4 px-5 py-3 text-sm">
                <span className="font-medium text-slate-800">{data.lawyer}</span>
                <span className="flex gap-6 text-right">
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.12em] text-slate-400">Processos</span>
                    <span className="font-semibold tabular-nums text-slate-900">{data.casesCount}</span>
                  </span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.12em] text-slate-400">Tempo médio</span>
                    <span className="font-semibold tabular-nums text-slate-900">{data.averageResolutionTime} dias</span>
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;

